import { Broker, CodeDeployment } from '@amzn/amazonmq-opsconsole-client';
import React, { useState, useEffect } from 'react';

import { Button, Container, Header, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-react';
import { useWorkflow } from './hooks/useWorkflow';
import { useAsg } from './hooks/useAsg';
import { useCodeDeploy } from './hooks/useCodeDeploy';
import { useRestartEvents } from './hooks/useRestartEvents';
import { RebootEvent } from '../../../types';
import { copyMarkdown } from './utils';
import AsgComponent from './AutoScalingGroup/AsgComponent';
import { RestartSelector } from './SummaryHeader/RestartSelector';
import SummaryHeader from './SummaryHeader/SummaryHeader';
import WorkflowComponent from './Workflow/WorkflowComponent';

const pollingOptions: SelectProps.Option[] = [
    { value: '10000', label: '10 seconds' },
    { value: '60000', label: '1 minute' },
    { value: '300000', label: '5 minutes' },
    { value: '600000', label: '10 minutes' },
];

type Props = {
    broker: Broker
}

const RebootProgress: React.FC<Props> = ({ broker }) => {
    const [asgName, setAsgName] = useState<string>('');
    const [asgArn, setAsgArn] = useState<string>('');
    const [timestampFrom, setTimestampFrom] = useState<number>(0);
    const [timestampTo, setTimestampTo] = useState<number>(Date.now());
    const [latestDeployment, setLatestDeployment] = useState<CodeDeployment | undefined>(undefined);
    const [latestStep, setLatestStep] = useState<RebootEvent | undefined>(undefined);
    const [failedEvents, setFailedEvents] = useState<RebootEvent[]>([]);
    const [pollingInterval, setPollingInterval] = useState<number>(10000);
    const [refreshCnt, setRefreshCnt] = useState<number>(0);
    
    const handlePollingIntervalChange = ({ detail }: { detail: { selectedOption?: SelectProps.Option } }) => {
        if (detail.selectedOption && detail.selectedOption.value) {
            setPollingInterval(parseInt(detail.selectedOption.value));
        }
    };
    
    const handleRefresh = () => {
        setRefreshCnt(refreshCnt + 1);
    };
    
    const handleCopyMarkdown = () => {
        copyMarkdown(asgName, asgArn, latestDeployment, latestStep, failedEvents);
    };
    
    useEffect(() => {
        const timer = setInterval(() => {
            setRefreshCnt(prev => prev + 1);
        }, pollingInterval);
        
        return () => clearInterval(timer);
    }, [pollingInterval]);

    const { restarts, loading: restartEventsLoading, error: restartEventsError } = useRestartEvents({
        broker, 
        refreshCnt
    });

    const { events, executions, loading: workflowLoading, error: workflowError } = useWorkflow({
        broker,
        timestampFrom,
        timestampTo,
        refreshCnt,
    });
    
    const { instances, loading: asgLoading, error: asgError } = useAsg({
        broker,
        refreshCnt,
        setAsgName,
        setAsgArn,
    });
    
    const { deployments, loading: codeDeployLoading, error: codeDeployError } = useCodeDeploy({
        broker,
        refreshCnt,
        timestampFrom,
        timestampTo,
        setLatestDeployment,
    });

    return (
        <>
            <SpaceBetween direction='vertical' size='l'>
                <RestartSelector
                    restarts={restarts}
                    loading={restartEventsLoading}
                    error={restartEventsError}
                    setTimestampFrom={setTimestampFrom}
                    setTimestampTo={setTimestampTo}
                />
                {timestampFrom != 0 && (
                    <>
                        <Container header={(
                            <Header variant='h2'
                                actions={
                                    <SpaceBetween direction='horizontal' size='xs' alignItems='center'>
                                        Polling Interval: <Select
                                            options={pollingOptions}
                                            selectedOption={pollingOptions.find(option => option.value === pollingInterval.toString()) || pollingOptions[0]}
                                            onChange={handlePollingIntervalChange}
                                        />
                                        <Button iconName='copy' onClick={handleCopyMarkdown} />
                                        <Button iconName='refresh' onClick={handleRefresh}/>
                                    </SpaceBetween>}
                            >
                                Broker Restart Progress
                            </Header>
                        )}>
                            <SummaryHeader
                                broker={broker}
                                asgName={asgName}
                                asgArn={asgArn}
                                latestDeployment={latestDeployment}
                                latestWorkflowStep={latestStep}
                            />
                        </Container>
                        <AsgComponent
                            broker={broker}
                            instances={instances}
                            asgLoading={asgLoading}
                            asgError={asgError}
                            deployments={deployments}
                            codeDeployLoading={codeDeployLoading}
                            codeDeployError={codeDeployError}
                        />
                        <WorkflowComponent
                            events={events}
                            loading={workflowLoading}
                            error={workflowError}
                            executions={executions}
                            failedEvents={failedEvents}
                            setLatestStep={setLatestStep}
                            setFailedEvents={setFailedEvents}
                        />
                    </>
                )}
            </SpaceBetween>
        </>
    );
};

export default RebootProgress;
